import { Box } from '@chakra-ui/react';
import { useKFlags } from '@/hooks/use-k-flags';
import { AutomationPreviewBody } from './AutomationPreviewBody';
import { useStageAutomationRule } from './useStagesWithAutomation';
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';

export type StageAutomationPreviewProps = {
  // The stage the candidate will land in. Null/empty disables the fetch and
  // renders nothing.
  stageId: string | number | null | undefined;
  // Optional explicit stage name; falls back to the rule's own stage name
  // (`summary.ats_job_stage.name`) and then the generic "Stage" label.
  stageName?: string;
  dataTestId?: string;
};

// Self-contained automation preview for a single target stage. Fetches the
// stage's active workflow_rule and renders the read-only activity/timing
// summary only when the stage actually has automation. Each instance owns its
// own query, so it's safe to render one per selected stage in a list (e.g. the
// multi-job create-candidate flow) — something a single top-level hook can't do.
export const StageAutomationPreview = ({
  stageId,
  stageName,
  dataTestId = 'stage-automation-preview',
}: StageAutomationPreviewProps) => {
  const { feStageAutomation } = useKFlags();
  const { data: rule } = useStageAutomationRule(stageId, {
    enabled: feStageAutomation,
  });

  if (!feStageAutomation || (rule?.actions?.length ?? 0) === 0) return null;

  const resolvedStageName =
    stageName ??
    rule?.summary?.ats_job_stage?.name ??
    MOVE_WITH_AUTOMATION_STRINGS.fallback.stageName;

  return (
    <Box my="16px">
      <AutomationPreviewBody
        targetStageName={resolvedStageName}
        targetRule={rule}
        dataTestId={dataTestId}
      />
    </Box>
  );
};
